import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import {
  getNotifySubscriberContactMethodsForLoggedInUser,
  GetNotifySubscriberContactMethodsForLoggedInUserResponse,
} from "../../../queries/notify";
import identity from "../../../selectors/identity";
import { coreService } from "../../../services/coreService";
import { FirstArgumentType } from "../../../types/ArgumentTypes";
import { useAnonymousOrNormalNetworkHeaders } from "../../auth/hooks/useAnonymousOrNormalNetworkHeaders";
import { useAnonymousToken } from "../../auth/hooks/useAnonymousToken";

export function useNotifySubscriberContactMethodsForLoggedInUser({
  onSuccessfullyLinked = () => {},
}: {
  onSuccessfullyLinked?: (
    linkedSubscriberContactMethod: GetNotifySubscriberContactMethodsForLoggedInUserResponse[number],
  ) => void;
} = {}) {
  const { isLoggedIn: isLoggedInAnonymously, identifier, token, logout } = useAnonymousToken();
  const isLoggedIn = useSelector(identity.isLoggedIn);
  const isNotLoggedIn = !isLoggedIn && !isLoggedInAnonymously();

  const headers = useAnonymousOrNormalNetworkHeaders();

  const linkExistingSubscriberContactMethod =
    isLoggedIn && isLoggedInAnonymously() && token && identifier
      ? {
          anonymousToken: token,
          subscriberContactMethodId: +identifier,
        }
      : undefined;

  const { data: subscriberContactMethods, isLoading: isSubscriberContactMethodsLoading } = useQuery(
    buildNotifySubscriberContactMethodsQueryKey(
      { subscriberContactMethodId: +(identifier ?? -1), linkExistingSubscriberContactMethod },
      headers,
    ),
    getNotifySubscriberContactMethodsForLoggedInUser(headers),
    {
      queryFnParamsFilter(args) {
        return [args[1]];
      },
      keepPreviousData: true,
      enabled: !isNotLoggedIn,
      onSuccess: (data) => {
        if (!!linkExistingSubscriberContactMethod) {
          const mostRecentlyUpdatedContactMethod = data.sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          )[0];

          onSuccessfullyLinked(mostRecentlyUpdatedContactMethod);
          logout();
        }
      },
    },
  );

  if (isNotLoggedIn) {
    return {
      subscriberContactMethods: [],
      isContactMethodsLoading: false,
      isLoggedIn,
      isLoggedInAnonymously: isLoggedInAnonymously() && !isLoggedIn,
    };
  }

  return {
    subscriberContactMethods,
    isSubscriberContactMethodsLoading,
    isLoggedIn,
    isLoggedInAnonymously: isLoggedInAnonymously(),
  };
}

export function buildNotifySubscriberContactMethodsQueryKey(
  params?: FirstArgumentType<typeof coreService.notifySubscriberContactMethodPublicUpdate> | { id: number | null },
  headers?: any,
) {
  if (!params) {
    return "notifySubscriberContactMethods";
  }
  if (!headers) {
    return ["notifySubscriberContactMethods", params];
  }
  return ["notifySubscriberContactMethods", params, headers];
}
